import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/BlogPost.css";

const API_URL = process.env.REACT_APP_API_URL;

function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = !!localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API_URL}/api/blogs/${id}`)
      .then((res) => {
        if (!res.ok) { setNotFound(true); setLoading(false); return null; }
        return res.json();
      })
      .then((data) => {
        if (data) { setBlog(data); setLoading(false); }
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Delete this blog post?")) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/blogs/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) { alert("Blog deleted"); navigate("/blogs"); }
      else alert("Failed to delete");
    } catch {
      alert("Server error");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });

  if (loading) return <div className="blog-post-loading">Loading...</div>;

  if (notFound) return (
    <div className="blog-post-loading">
      <p>Blog not found.</p>
      <button className="back-btn" onClick={() => navigate("/blogs")}>
        ← Back to Blogs
      </button>
    </div>
  );

  return (
    <div className="blog-post-page">

      <Helmet>
        <title>{blog.title} | Biko Sphere Blog</title>
        <meta name="description" content={blog.summary} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.summary} />
        <meta property="og:url" content={`https://bikosphere.com/blogs/${id}`} />
        {blog.image && <meta property="og:image" content={blog.image} />}
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.summary} />
        {blog.image && <meta name="twitter:image" content={blog.image} />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              headline: blog.title,
              description: blog.summary,
              image: blog.image || "",
              author: {
                "@type": "Person",
                name: blog.author || "Biko",
              },
              datePublished: blog.createdAt,
              url: `https://bikosphere.com/blogs/${id}`,
            }),
          }}
        />
      </Helmet>

      <div className="blog-post-content">

        <div className="blog-post-topbar">
          <button className="back-btn" onClick={() => navigate("/blogs")}>
            ← Back to Blogs
          </button>
          {isAdmin && (
            <div className="admin-post-actions">
              <button
                className="edit-btn"
                onClick={() => navigate(`/admin/dashboard?edit=${id}`)}
              >
                ✏️ Edit
              </button>
              <button
                className="delete-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "🗑 Delete"}
              </button>
            </div>
          )}
        </div>

        {blog.image && (
          <div className="blog-post-image">
            <img src={blog.image} alt={blog.title} />
          </div>
        )}

        <div className="blog-post-meta">
          <span className="blog-tag"> blog</span>
          <span className="blog-date">{formatDate(blog.createdAt)}</span>
          {blog.author && <span className="blog-author">by {blog.author}</span>}
        </div>

        <h1 className="blog-post-title">{blog.title}</h1>
        <p className="blog-post-summary">{blog.summary}</p>

        <div className="blog-post-divider" />

        {blog.sections?.map((section, i) => (
          <div key={i} className="blog-section">
            <h2>{section.heading}</h2>
            <p>{section.body}</p>
          </div>
        ))}

        {blog.conclusion && (
          <div className="blog-conclusion">
            <h2>Conclusion</h2>
            <p>{blog.conclusion}</p>
          </div>
        )}

      </div>
    </div>
  );
}

export default BlogPost;